import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, orderBy, doc, addDoc, updateDoc, writeBatch, increment, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import app from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext'; // Import useAuth

// Import all the sub-components
import ProfileSummary from './ProfileSummary';
import PerformanceChart from './PerformanceChart'; 
import MilkProductionTracker from '../milk/MilkProductionTracker';
import HealthTracker from '../health/HealthTracker';
import CowFeedHistory from '../feed/CowFeedHistory';
import AllocateFeedModal from '../feed/AllocateFeedModal';
import MilkRecordFormModal from '../milk/MilkRecordFormModal';
import HealthRecordFormModal from '../health/HealthRecordFormModal';

const __app_id = app.options.appId;

// Helper function for local date string
const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const LivestockProfile = ({ animal, onBack, onEdit, db, storage, feedInventory }) => {
    const { currentUser } = useAuth(); // Get current user from context
    const farmId = currentUser.farmId; // Get farmId
    const userId = currentUser.uid; // Keep userId for storage paths if needed

    const [profileView, setProfileView] = useState('summary');
    const [milkRecords, setMilkRecords] = useState([]);
    const [healthRecords, setHealthRecords] = useState([]);
    const [feedRecords, setFeedRecords] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAllocateFeedModalOpen, setAllocateFeedModalOpen] = useState(false);
    const [editingMilkRecord, setEditingMilkRecord] = useState(null);
    const [editingHealthRecord, setEditingHealthRecord] = useState(null);

    const isInactive = animal.status === 'Sold' || animal.status === 'Dead';

    useEffect(() => {
        if (!db || !farmId || !animal.id) return;
        // --- NEW: Path uses farmId ---
        const getPath = (recordType) => `/artifacts/${__app_id}/farms/${farmId}/livestock/${animal.id}/${recordType}`;
        
        const unsubMilk = onSnapshot(query(collection(db, getPath('milk_records')), orderBy('date', 'desc')), (snap) => setMilkRecords(snap.docs.map(d => ({id: d.id, ...d.data()}))));
        const unsubHealth = onSnapshot(query(collection(db, getPath('health_records')), orderBy('date', 'desc')), (snap) => setHealthRecords(snap.docs.map(d => ({id: d.id, ...d.data()}))));
        const unsubFeed = onSnapshot(query(collection(db, getPath('feed_allocations')), orderBy('date', 'desc')), (snap) => setFeedRecords(snap.docs.map(d => ({id: d.id, ...d.data()}))));

        return () => { unsubMilk(); unsubHealth(); unsubFeed(); };
    }, [db, farmId, animal.id]); // --- NEW: Dependency is farmId ---

    const handleRecordSubmit = async (recordData) => {
        if (isInactive || !farmId) return;
        setIsSubmitting(true);
        // --- NEW: Path uses farmId ---
        const path = `/artifacts/${__app_id}/farms/${farmId}/livestock/${animal.id}/milk_records`;
        const dataToSave = { 
            ...recordData, 
            morningYield: Number(recordData.morningYield) || 0, 
            eveningYield: Number(recordData.eveningYield) || 0, 
            fatPercent: Number(recordData.fatPercent) || 0, 
            snfPercent: Number(recordData.snfPercent) || 0,
            pricePerLiter: Number(recordData.pricePerLiter) || 0
        };
        try {
            if(editingMilkRecord) {
                await updateDoc(doc(db, path, editingMilkRecord.id), dataToSave);
                setEditingMilkRecord(null);
            } else {
                // --- NEW: Add farmId and userId to the new document ---
                await addDoc(collection(db, path), { ...dataToSave, farmId, userId, createdAt: serverTimestamp() });
            }
        } catch(e) { console.error("Failed to save milk record:", e); } finally { setIsSubmitting(false); }
    };
    
    const handleEventSubmit = async (eventData) => {
        if (isInactive || !farmId) return;
        setIsSubmitting(true);
        // --- NEW: Path uses farmId ---
        const path = `/artifacts/${__app_id}/farms/${farmId}/livestock/${animal.id}/health_records`;
        let imageUrl = editingHealthRecord ? editingHealthRecord.imageUrl : '';
        if (eventData.image) {
            const imageRef = ref(storage, `health_records/${farmId}/${animal.id}/${Date.now()}_${eventData.image.name}`);
            await uploadBytes(imageRef, eventData.image);
            imageUrl = await getDownloadURL(imageRef);
        }
        const { image, ...dataToSave } = eventData;
        try {
            if(editingHealthRecord) {
                await updateDoc(doc(db, path, editingHealthRecord.id), { ...dataToSave, imageUrl, cost: Number(eventData.cost) || 0 });
                setEditingHealthRecord(null);
            } else {
                // --- NEW: Add farmId and userId to the new document ---
                await addDoc(collection(db, path), { ...dataToSave, imageUrl, cost: Number(eventData.cost) || 0, farmId, userId, createdAt: serverTimestamp() });
            }
        } catch(e) { console.error("Failed to save health event:", e); } finally { setIsSubmitting(false); }
    };

    const handleAllocateFeed = async (allocationData) => {
        if (isInactive || !farmId) return;
        setIsSubmitting(true);
        const { feedItemId, quantity } = allocationData;
        const quantityNum = Number(quantity);
        const selectedFeed = feedInventory.find(f => f.id === feedItemId);

        if (!selectedFeed || isNaN(quantityNum) || quantityNum <= 0 || selectedFeed.stock < quantityNum) {
            console.error("Invalid feed allocation data or insufficient stock.");
            setIsSubmitting(false);
            return;
        }

        // --- NEW: Paths use farmId ---
        const feedInventoryRef = doc(db, `/artifacts/${__app_id}/farms/${farmId}/feed_inventory`, feedItemId);
        const feedAllocationRef = collection(db, `/artifacts/${__app_id}/farms/${farmId}/livestock/${animal.id}/feed_allocations`);
        
        try {
            const batch = writeBatch(db);
            batch.update(feedInventoryRef, { stock: increment(-quantityNum) });
            const newAllocationDoc = doc(feedAllocationRef);
            // --- NEW: Add farmId and userId to the new document ---
            batch.set(newAllocationDoc, {
                date: getLocalDateString(new Date()),
                feedItemId,
                feedItemName: selectedFeed.name,
                quantity: quantityNum,
                unitPrice: selectedFeed.unitPrice,
                cost: (selectedFeed.unitPrice || 0) * quantityNum,
                farmId,
                userId,
                createdAt: serverTimestamp(),
            });
            await batch.commit();
            setAllocateFeedModalOpen(false);
        } catch (e) {
            console.error("Failed to allocate feed:", e);
        } finally {
            setIsSubmitting(false);
        }
    };

    // The rest of the component's JSX remains the same
    return (
        <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
            <div className="flex flex-col sm:flex-row justify-between items-start mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">{animal.name}</h2>
                    <p className="text-sm text-gray-500 mt-1">Tag #: {animal.tagNumber}</p>
                </div>
                <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                    <button 
                        onClick={() => setAllocateFeedModalOpen(true)} 
                        disabled={isInactive}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        title={isInactive ? `This animal is ${animal.status}` : 'Allocate Feed'}
                    >
                        Allocate Feed
                    </button>
                    <button onClick={() => onEdit(animal)} className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700">Edit Animal</button>
                    <button onClick={onBack} className="text-sm text-indigo-600 hover:text-indigo-800">&larr; Back to Herd</button>
                </div>
            </div>
            
            <div className="mt-4">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-6 overflow-x-auto">
                        <button onClick={() => setProfileView('summary')} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${profileView === 'summary' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Summary</button>
                        <button onClick={() => setProfileView('performance')} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${profileView === 'performance' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Performance Chart</button>
                        <button onClick={() => setProfileView('milk_details')} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${profileView === 'milk_details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Milk</button>
                        <button onClick={() => setProfileView('health_details')} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${profileView === 'health_details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Health</button>
                        <button onClick={() => setProfileView('feed_details')} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${profileView === 'feed_details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Feed</button>
                    </nav>
                </div>

                <div className="mt-6">
                    {profileView === 'summary' && 
                        <ProfileSummary 
                            milkRecords={milkRecords} 
                            healthRecords={healthRecords} 
                            feedRecords={feedRecords} 
                            onViewDetails={setProfileView}
                        />
                    }
                    {profileView === 'performance' && <PerformanceChart milkRecords={milkRecords} healthRecords={healthRecords} feedRecords={feedRecords} />}
                    {profileView === 'milk_details' && <MilkProductionTracker records={milkRecords} onRecordSubmit={handleRecordSubmit} onEditRecord={setEditingMilkRecord} isSubmitting={isSubmitting} isInactive={isInactive} animalStatus={animal.status} />}
                    {profileView === 'health_details' && <HealthTracker records={healthRecords} onEventSubmit={handleEventSubmit} onEditEvent={setEditingHealthRecord} isSubmitting={isSubmitting} isInactive={isInactive} animalStatus={animal.status} />}
                    {profileView === 'feed_details' && <CowFeedHistory records={feedRecords} />}
                </div>
            </div>

            <AllocateFeedModal isOpen={isAllocateFeedModalOpen} onClose={() => setAllocateFeedModalOpen(false)} onSubmit={handleAllocateFeed} isLoading={isSubmitting} feedInventory={feedInventory} />
            {editingMilkRecord && <MilkRecordFormModal isOpen={!!editingMilkRecord} onClose={() => setEditingMilkRecord(null)} onSubmit={handleRecordSubmit} isLoading={isSubmitting} initialData={editingMilkRecord} />}
            {editingHealthRecord && <HealthRecordFormModal isOpen={!!editingHealthRecord} onClose={() => setEditingHealthRecord(null)} onSubmit={handleEventSubmit} isLoading={isSubmitting} initialData={editingHealthRecord} />}
        </div>
    );
};

export default LivestockProfile;
